import React, { useEffect, useState } from "react";
import getCapitalizedLocale from '../../helpers/get-capitalized-locale';
import EmailRequest from "./EmailRequest";
import RegistrationRequest from "./RegistrationRequest";
import EmailPasswordRequest from "./EmailPasswordRequest";
import {
    EmailRequestHeader,
    RegistrationHeader,
    RegistrationSuccessHeader,
    RegistrationSuccessHeaderWithPreModeration
} from "./RegistrationHeader";
import UIButton from "../../../../globalComponents/UIComponents/UIButton";
import { checkPhone, sendSmsCode } from "../../utils/api";
import SmsPasswordRequest from "./SmsPasswordRequest";
import { TEST_EMAIL } from "../../constants/common";

const ROLE_REQUEST = 'request';

export default ({
    preview,
    previewComponent,
    $locale,
    eventKey,
    moduleAuth = {},
    registrationFields,
    onWindowResize,
    participationType,
    eventName,
    onRegister,
    eventPremoderation,
    moduleSettings,
    timeStart,
    hideInfoBlock,
    corporateMail,
    participationFormatLabels,
    customDescription,
    externalServices,
    ssoIconUrl,
    internalLoginEnabled,
    authRequired,
    loginWithPhoneEnabled,
    registrationType,
}) => {
    const [email, setEmail] = useState(null);
    const [isRegistered, setRegistered] = useState(false);
    const [isRegisteredNow, setRegisteredNow] = useState(false);
    const [loginByPhone, setLoginByPhone] = useState(false);
    const [passwordUpdateLock, setPasswordUpdateLock] = useState(null);
    const [requiredFields, setRequiredFields] = useState(preview ? registrationFields : []);
    const [phoneAuthAllowed, setPhoneAuthAllowed] = useState(false);
    const [isHintVisible, setHintVisible] = useState(false);
    const [participationTypeSet, setParticipationType] = useState(false);
    const [userRole, setUserRole] = useState('');

    const localeName = $locale.localeName ?? $locale.name;
    const localeNameInPascalCase = getCapitalizedLocale(localeName);
    const registrationSubtitle = moduleSettings[`registrationSubtitle${localeNameInPascalCase}`];

    useEffect(() => {
        if (onWindowResize) {
            onWindowResize();
        }
    }, [email, isRegistered]);

    const handleEmailRequest = async (response) => {
        const requiredFields = response.requiredFields ?? [];

        setEmail(response.email);
        setPasswordUpdateLock(response.passwordUpdateLock);
        setRegistered(response.status);
        onRegister(response.status);
        setUserRole(response.role);
        setRequiredFields(registrationFields.filter(({ name }) => requiredFields?.includes(name)));
        setPhoneAuthAllowed(response.phoneAuthAllowed)
        setHintVisible(response.registered && response.requiredFields?.length);
        setParticipationType(response.registered && !!response.participationType);
    };

    const handleRegistration = async ({ passwordLockTime }) => {
        setPasswordUpdateLock(passwordLockTime);
        setRegistered(true);
        setRegisteredNow(true);
        onRegister(true);
    }

    const handleLogin = (client) => { }

    const handleEmailReset = async () => {
        if (preview) return;

        setEmail(null);
        setRegistered(false);
        setRegisteredNow(false)
    }

    const handleCloseAuthentication = () => {
        handleEmailReset();
        setRegistered(false);
        setRegisteredNow(false);
        onRegister(false);
    }

    const handleSendSMS = async () => {
        try {
            const response = await sendSmsCode(eventKey, { email });
            setUserRole(response.role);
            setRegistered(response.status);
            setLoginByPhone(response.status);
            setPasswordUpdateLock(response.passwordUpdateLock);
        } catch (e) {
            console.error(e)
        }
    }

    if (loginByPhone) {
        return <SmsPasswordRequest
            eventKey={eventKey}
            email={email}
            hideInfoBlock={hideInfoBlock}
            passwordUpdateLock={passwordUpdateLock}
            setPasswordUpdateLock={setPasswordUpdateLock}
            $locale={$locale}
            onSuccess={handleLogin}
            handleSendSMS={handleSendSMS}
        />
    }

    if (previewComponent === 'regSuccess' || (email && isRegistered)) {
        if (eventPremoderation && (preview || userRole === ROLE_REQUEST)) {
            return (
                <RegistrationSuccessHeaderWithPreModeration
                    eventName={eventName}
                    email={preview ? TEST_EMAIL : email}
                    $locale={$locale}
                    hideAcceptedTitle={moduleSettings.hideAcceptedTitle}
                    acceptedTitle={moduleSettings[`acceptedTitle${localeNameInPascalCase}`]}
                    hideAcceptedSubtitle={moduleSettings.hideAcceptedSubtitle}
                    acceptedSubtitle={moduleSettings[`acceptedSubtitle${localeNameInPascalCase}`]}
                >
                    <UIButton
                        type="button"
                        size="large"
                        color="primary"
                        onClick={handleCloseAuthentication}
                    >
                        {$locale.get('guide.exit')}
                    </UIButton>
                </RegistrationSuccessHeaderWithPreModeration>
            );
        }

        return (
            <RegistrationSuccessHeader
                email={preview ? TEST_EMAIL : email}
                isRegisteredNow={isRegisteredNow || preview}
                $locale={$locale}
                hideSuccessTitle={moduleSettings.hideSuccessTitle}
                successTitle={moduleSettings[`successTitle${localeNameInPascalCase}`]}
                hideSuccessSubtitle={moduleSettings.hideSuccessSubtitle}
                successSubtitle={moduleSettings[`successSubtitle${localeNameInPascalCase}`]}
                hideInfoBlock={hideInfoBlock}
            >
                <EmailPasswordRequest
                    preview={preview}
                    $locale={$locale}
                    eventKey={eventKey}
                    phoneAuthAllowed={phoneAuthAllowed}
                    handleSendSMS={handleSendSMS}
                    email={preview ? TEST_EMAIL : email}
                    onSuccess={handleLogin}
                    onReset={handleEmailReset}
                    passwordUpdateLock={passwordUpdateLock}
                />
            </RegistrationSuccessHeader>
        );
    }

    if (email || previewComponent === 'regFields') {
        return (
            <RegistrationHeader
                customDescription={customDescription}
                $locale={$locale}
                hideInfoBlock={hideInfoBlock}
            >
                {(!moduleSettings.hideRegistrationSubtitle && registrationSubtitle) && (
                    <p>{registrationSubtitle}</p>
                )}
                {isHintVisible && (
                    <p>{$locale.get('registration.hintFillFields')}</p>
                )}
                <RegistrationRequest
                    preview={preview}
                    $locale={$locale}
                    eventKey={eventKey}
                    allowAnonymousLogin={!moduleAuth?.require && moduleAuth?.anonymous}
                    requireAuthPhone={moduleAuth?.phone}
                    email={email}
                    participationType={participationType}
                    participationTypeSet={participationTypeSet}
                    fields={requiredFields}
                    eventPremoderation={eventPremoderation}
                    participationFormatLabels={participationFormatLabels}
                    registrationType={registrationType}
                    localeNameInPascalCase={localeNameInPascalCase}
                    authRequired={authRequired}
                    onSuccess={handleRegistration}
                    onReset={handleEmailReset}
                />
            </RegistrationHeader>
        );
    }

    return (
        <EmailRequestHeader
            eventName={eventName}
            timeStart={timeStart}
            hideInfoBlock={hideInfoBlock}
            $locale={$locale}
            customDescription={customDescription || moduleSettings[`loginSubtitle${localeNameInPascalCase}`]}
            hideLoginSubtitle={moduleSettings.hideLoginSubtitle}
            enterOnly={moduleSettings.enterOnly}
            authRequired={authRequired}
        >
            <EmailRequest
                $locale={$locale}
                eventKey={eventKey}
                allowAnonymousLogin={!moduleAuth?.require && moduleAuth?.anonymous}
                enterOnly={moduleSettings.enterOnly}
                corporateMail={corporateMail}
                onSuccess={handleEmailRequest}
                onWindowResize={onWindowResize}
            />
        </EmailRequestHeader>
    )
}
